import { computed, reactive } from 'vue';
import { useQuery } from 'vue-query';
import { QueryObserverOptions } from 'react-query/core';
import useTokens from '@/composables/useTokens';
import QUERY_KEYS from '@/constants/queryKeys';
import { getAddress } from '@ethersproject/address';
import { balancerContractsService } from '@/services/balancer/contracts/balancer-contracts.service';
import { balancerFarmSubgraphService } from '@/services/balancer/subgraph/balancer-farm-subgraph.service';
import { balancerSubgraphService } from '@/services/balancer/subgraph/balancer-subgraph.service';
import { FullPool, Pool } from '@/services/balancer/subgraph/types';
import { POOLS } from '@/constants/pools';
import useApp from '../useApp';
import useUserSettings from '../useUserSettings';
// import useWeb3 from '@/services/web3/useWeb3';
import { forChange } from '@/lib/utils';
import useWeb3 from '@/services/web3/useWeb3';
import useNetwork from '../useNetwork';
// import { isManaged, isStableLike } from '../usePool';

export default function useFarmPoolQuery(
  id: string,
  options: QueryObserverOptions<FullPool> = {}
) {
  /**
   * COMPOSABLES
   */
  const { getTokens, injectTokens, prices, dynamicDataLoading } = useTokens();
  const { appLoading } = useApp();
  const { appNetworkConfig } = useWeb3();
  const { currency } = useUserSettings();
  const { networkId } = useNetwork();

  /**
   * COMPUTED
   */
  const enabled = computed(
    () => !appLoading.value && !dynamicDataLoading.value
  );

  /**
   * METHODS
   */
  // function isBlocked(pool: Pool): boolean {
  //   const requiresAllowlisting =
  //     isStableLike(pool.poolType) || isManaged(pool.poolType);
  //   const isOwnedByUser =
  //     isAddress(account.value) &&
  //     getAddress(pool.owner) === getAddress(account.value);
  //   const isAllowlisted =
  //     POOLS.Stable.AllowList.includes(id) ||
  //     POOLS.Investment.AllowList.includes(id);

  //   return requiresAllowlisting && !isAllowlisted && !isOwnedByUser;
  // }

  /**
   * QUERY INPUTS
   */
  const queryKey = QUERY_KEYS.FarmPools.Current(id);
  const queryFn = async () => {
    const [pool] = await balancerFarmSubgraphService.farmPools.get({
      where: {
        pid: id.split('_')[1]
      }
    });
    const investPool = await balancerSubgraphService.pools.get({
      where: {
        address: id.split('_')[0]
      }
    });
    if (investPool.length) {
      Object.assign(pool, investPool[0], { isSingleFarm: false });
    } else {
      const singleFarm = POOLS.SingleFarm[networkId.value];
      const farmInfo = singleFarm[`${pool.lpToken}_${pool.pid}`];
      Object.assign(pool, { isSingleFarm: true }, farmInfo);
    }

    // Inject relevant pool tokens to fetch metadata
    await injectTokens([pool.lpToken]);
    await forChange(dynamicDataLoading, false);

    // Need to fetch onchain pool data first so that calculations can be
    // performed in the decoration step.
    const poolTokenMeta = getTokens(
      pool.tokensList.map(address => getAddress(address))
    );

    const farmingInfo = await balancerFarmSubgraphService.farmingInfo.get({
      id: '1'
    });

    const onchainData = await balancerContractsService.vault.getFarmPoolData(
      pool.id,
      pool.poolType,
      poolTokenMeta,
      !investPool.length ? pool.poolTokens : undefined
    );
    if (!investPool.length) {
      const singleFarm = POOLS.SingleFarm[networkId.value];
      const farmInfo = singleFarm[`${pool.lpToken}_${pool.pid}`];
      onchainData.tokens = { [pool.lpToken]: farmInfo.tokens[0] };
    }
    const [
      decoratedPool
    ] = await balancerFarmSubgraphService.farmPools.decorate(
      [{ ...pool, onchain: onchainData }],
      '24h',
      prices.value,
      currency.value,
      farmingInfo,
      appNetworkConfig.addresses.flake
    );

    let unwrappedTokens: Pool['unwrappedTokens'];
    return { onchain: onchainData, unwrappedTokens, ...decoratedPool };
  };

  const queryOptions = reactive({
    enabled,
    ...options
  });

  return useQuery<FullPool>(queryKey, queryFn, queryOptions);
}
